// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Site-wide motion layer for the public marketing + public form pages:
//   â€¢ HIBANE androids + section props (lazy WebGL chunk) â€” see Cast.tsx.
//     Pages declare slots: [data-robot-stage] sections (or [data-hero3d]) hold
//     [data-actor="male|female"] slots (data-pose, data-face, data-scrub) and
//     [data-prop="globe|â€¦"] slots (data-step). Legacy [data-robot-anchor]
//     slots are treated as data-actor="male".
//   â€¢ scroll reveals applied automatically to every page (useAutoReveal)
//   â€¢ 3D pointer tilt on cards, scroll progress rail, trailing cursor ring
// Everything is skipped for prefers-reduced-motion and on portal/admin routes.
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fx, prefersReducedMotion, supportsWebGL, type ActorTarget, type PropTarget } from "./fxStore";
import { isProp } from "./android/props";
import { useAutoReveal } from "./useAutoReveal";

const RobotStage = lazy(() => import("./RobotStage"));

/** Routes that keep their own UI: portals, admin, client-branded and token pages. */
const EXCLUDED = [
  /^\/splash/, // splash has its own intro (Home lives at "/")
  /^\/studiopackage/, // studio package flow has its own full-screen layout
  /^\/pay\//, // payment pages
  /^\/admin/,
  /^\/hr(\/|$)/,
  /^\/finance(\/|$)/,
  /^\/voice(\/|$)/,
  /^\/socials/,
  /^\/shared\//,
  /^\/f\//,
  /^\/auth\//,
  /^\/oauth\//,
  /^\/welcome\//,
  /^\/account\//,
  /^\/upgrade/,
  /^\/media-delivery\//,
  /^\/media-review\//,
  /^\/d\//,
];

export const isExperienceRoute = (path: string) => !EXCLUDED.some((r) => r.test(path));
/** Routes that use the public cinematic theme (html[data-cine]); The Edge keeps its own brand. */
export const isCineRoute = (path: string) => isExperienceRoute(path) && !/^\/theedge/.test(path);

let slotSeq = 0;
const slotKey = (el: HTMLElement) => (el.dataset.fxKey ||= `s${++slotSeq}`);

/** Reads actor + prop slots from the stage crossing the viewport centre. */
function measureStage() {
  const mid = window.innerHeight / 2;
  const actors: Record<"male" | "female", ActorTarget | null> = { male: null, female: null };
  const props: PropTarget[] = [];
  let stage: HTMLElement | null = null;
  document.querySelectorAll<HTMLElement>("main [data-robot-stage], main [data-hero3d]").forEach((s) => {
    const r = s.getBoundingClientRect();
    if (!stage && r.top <= mid && r.bottom >= mid) stage = s;
  });
  if (!stage) return { actors, props };
  const st = stage as HTMLElement;
  const sr = st.getBoundingClientRect();
  const p = Math.min(1, Math.max(0, (mid - sr.top) / Math.max(1, sr.height)));

  st.querySelectorAll<HTMLElement>("[data-actor], [data-robot-anchor]").forEach((el) => {
    const who = (el.dataset.actor as "male" | "female" | undefined) ?? "male";
    if (actors[who]) return;
    const r = el.getBoundingClientRect();
    if (r.height <= 0 || r.width <= 0) return;
    actors[who] = {
      key: slotKey(el),
      x: r.left + r.width / 2,
      y: r.bottom,
      h: r.height,
      pose: el.dataset.pose,
      face: Number(el.dataset.face ?? 0) || 0,
      p,
      turn: el.dataset.scrub === "turn",
    };
  });
  st.querySelectorAll<HTMLElement>("[data-prop]").forEach((el) => {
    const name = el.dataset.prop;
    const r = el.getBoundingClientRect();
    if (!isProp(name) || r.height <= 0) return;
    props.push({ key: slotKey(el), name, x: r.left + r.width / 2, y: r.top + r.height / 2, w: r.width, h: r.height, p, step: Number(el.dataset.step ?? 0) || 0 });
  });
  return { actors, props };
}

const FIELD = "input:not([type=hidden]):not([type=checkbox]):not([type=radio]), textarea, select";
const TILT = "[data-tilt], [data-fx-reveal='tilt'][data-fx-done]";

const toNdc = (x: number, y: number) => ({
  x: (x / window.innerWidth) * 2 - 1,
  y: -(y / window.innerHeight) * 2 + 1,
});

/** Width of the empty space left of the visible form, or 0 if there is none. */
function measureFormGutter(): number {
  const vh = window.innerHeight;
  let best: HTMLFormElement | null = null;
  let bestArea = 0;
  document.querySelectorAll<HTMLFormElement>("main form").forEach((f) => {
    const r = f.getBoundingClientRect();
    const visible = Math.max(0, Math.min(r.bottom, vh * 0.85) - Math.max(r.top, vh * 0.15));
    if (visible * r.width > bestArea) {
      bestArea = visible * r.width;
      best = f;
    }
  });
  if (!best) return 0;
  const form = best as HTMLFormElement;
  const gutter = form.getBoundingClientRect().left;
  const left = fx.insetLeft;
  if (gutter - left < 240) return 0;
  // Only use the gutter if nothing but the form's own ancestors sits there.
  for (const fx_ of [0.25, 0.5, 0.75]) {
    for (const fy of [0.35, 0.5, 0.65]) {
      const el = document.elementFromPoint(left + (gutter - left) * fx_, vh * fy);
      if (el && !el.contains(form)) return 0;
    }
  }
  return gutter;
}

export default function Experience3D() {
  const { pathname } = useLocation();
  const enabled = isExperienceRoute(pathname) && !prefersReducedMotion();
  const [webgl] = useState(() => typeof document !== "undefined" && supportsWebGL());
  const [hidden, setHidden] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);

  useAutoReveal(enabled, pathname);

  // Route arrival.
  useEffect(() => {
    if (!enabled) return;
    const now = performance.now();
    fx.arrivedAt = now;
    fx.waveAt = now + 1100;
    fx.focus = null;
  }, [enabled, pathname]);

  // Hide the robot while a modal dialog is open (it would float above it).
  useEffect(() => {
    if (!enabled) return;
    const check = () => setHidden(!!document.querySelector("[role='dialog'][data-state='open'], [role='alertdialog']"));
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    check();
    return () => mo.disconnect();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    let focusEl: HTMLElement | null = null;
    let tiltEl: HTMLElement | null = null;
    const mouse = { x: -100, y: -100 };
    const ring = { x: -100, y: -100, s: 1 };
    let ringTarget = 1;
    let frame = 0;
    let raf = 0;

    const resetTilt = () => {
      if (!tiltEl) return;
      const el = tiltEl;
      el.style.transition = "transform 600ms cubic-bezier(0.16, 1, 0.3, 1)";
      el.style.transform = "";
      window.setTimeout(() => {
        if (tiltEl !== el) el.style.transition = "";
      }, 650);
      tiltEl = null;
    };

    const onPointerMove = (e: PointerEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      const p = toNdc(e.clientX, e.clientY);
      fx.pointer.x = p.x;
      fx.pointer.y = p.y;

      const target = e.target as Element | null;
      ringTarget = target?.closest?.("a, button, [role=button], input, textarea, select, label") ? 2.2 : 1;

      if (!finePointer) return;
      const card = target?.closest?.(TILT) as HTMLElement | null;
      const tiltable = card && card.closest("main") && !card.querySelector(FIELD) && card.offsetWidth < 760;
      if (!tiltable) return resetTilt();
      if (card !== tiltEl) {
        resetTilt();
        tiltEl = card;
        card.style.transition = "transform 160ms ease-out";
      }
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      card.style.transform = `perspective(900px) rotateX(${(-py * 9).toFixed(2)}deg) rotateY(${(px * 11).toFixed(2)}deg) translateZ(0) translateY(-4px)`;
    };

    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement;
      if (el?.matches?.(FIELD) && el.closest("main")) {
        focusEl = el;
        fx.waveAt = 0;
      }
    };
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as HTMLElement | null;
      if (!next?.matches?.(FIELD)) {
        focusEl = null;
        fx.focus = null;
      }
    };
    const onKey = () => {
      if (focusEl) fx.typingAt = performance.now();
    };

    const tick = () => {
      raf = requestAnimationFrame(tick);
      frame++;
      const doc = document.documentElement;
      fx.scrollY = window.scrollY;
      const max = doc.scrollHeight - window.innerHeight;
      fx.scroll = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (railRef.current) railRef.current.style.transform = `scaleX(${fx.scroll})`;

      const stage = measureStage();
      fx.actors = stage.actors;
      fx.props = stage.props;
      // outros don't count: a form page ending in one keeps its assistant android
      fx.hasStages = !!document.querySelector('main [data-robot-stage]:not([data-robot-stage="outro"])');
      fx.insetLeft = document.querySelector<HTMLElement>("[data-fx-inset-left]")?.getBoundingClientRect().right ?? 0;
      const dock = document.querySelector<HTMLElement>("[data-fx-inset-bottom]")?.getBoundingClientRect();
      fx.insetBottom = dock && dock.height > 0 ? window.innerHeight - dock.top : 0;

      if (focusEl) {
        const r = focusEl.getBoundingClientRect();
        fx.focus = toNdc(r.left + Math.min(r.width, 240) / 2, r.top + r.height / 2);
      }
      if (frame % 8 === 0) fx.formGutter = measureFormGutter();

      if (finePointer && dotRef.current && ringRef.current) {
        ring.x += (mouse.x - ring.x) * 0.18;
        ring.y += (mouse.y - ring.y) * 0.18;
        ring.s += (ringTarget - ring.s) * 0.15;
        dotRef.current.style.transform = `translate3d(${mouse.x}px, ${mouse.y}px, 0)`;
        ringRef.current.style.transform = `translate3d(${ring.x}px, ${ring.y}px, 0) scale(${ring.s})`;
      }
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("keydown", onKey);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      resetTilt();
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("keydown", onKey);
      fx.focus = null;
      fx.formGutter = 0;
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div aria-hidden className="fx-rail pointer-events-none fixed left-0 right-0 top-0 z-[60] h-[2px] origin-left">
        <div ref={railRef} className="h-full w-full origin-left bg-[#2A629E]" style={{ transform: "scaleX(0)" }} />
      </div>
      <div ref={dotRef} aria-hidden className="fx-cursor-dot" />
      <div ref={ringRef} aria-hidden className="fx-cursor-ring" />
      {webgl && (
        <Suspense fallback={null}>
          <div style={{ opacity: hidden ? 0 : 1, transition: "opacity 300ms" }}>
            <RobotStage paused={hidden} />
          </div>
        </Suspense>
      )}
    </>
  );
}
